import { useState, useRef, useEffect, useCallback, useMemo } from 'react'

import type { SelectOption } from './types'

interface UseDropdownPositionOptions {
  isOpen: boolean
  isMobile: boolean
  modal: boolean
  filteredOptionsCount: number
  itemHeight: number
  listHeight: number
}

export function useDropdownPosition({
  isOpen,
  isMobile,
  modal,
  filteredOptionsCount,
  itemHeight,
  listHeight,
}: UseDropdownPositionOptions): {
  containerRef: React.RefObject<HTMLDivElement>
  dropdownPosition: { top: number; left: number; width: number } | null
} {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null)

  useEffect(() => {
    if (!isOpen || isMobile || modal) { return }

    const updatePosition = (): void => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        const estimatedDropdownHeight = Math.min(filteredOptionsCount * itemHeight + 60, listHeight + 60)
        const spaceBelow = window.innerHeight - rect.bottom
        const spaceAbove = rect.top
        const shouldPositionAbove = spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow

        /* v8 ignore next 6 */
        if (shouldPositionAbove) {
          setDropdownPosition({
            top: rect.top + window.scrollY - estimatedDropdownHeight - 4,
            left: rect.left + window.scrollX,
            width: rect.width,
          })
        } else {
          setDropdownPosition({
            top: rect.bottom + window.scrollY + 4,
            left: rect.left + window.scrollX,
            width: rect.width,
          })
        }
      }
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen, isMobile, modal, filteredOptionsCount, itemHeight, listHeight])

  return { containerRef, dropdownPosition }
}

interface UseOutsideClickOptions {
  isOpen: boolean
  isMobile: boolean
  onClose: () => void
}

export function useOutsideClick(
  containerRef: React.RefObject<HTMLDivElement>,
  dropdownRef: React.RefObject<HTMLDivElement>,
  { isOpen, isMobile, onClose }: UseOutsideClickOptions
): void {
  useEffect(() => {
    if (isMobile) { return }

    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, isMobile, containerRef, dropdownRef, onClose])
}

interface UseVirtualizationOptions {
  filteredOptionsCount: number
  itemHeight: number
  maxVisibleItems: number
  virtualized: boolean
  scrollTop: number
}

export function useVirtualization({
  filteredOptionsCount,
  itemHeight,
  maxVisibleItems,
  virtualized,
  scrollTop,
}: UseVirtualizationOptions): {
  totalHeight: number
  visibleCount: number
  listHeight: number
  startIndex: number
  endIndex: number
  offsetY: number
} {
  return useMemo(() => {
    const totalHeight = filteredOptionsCount * itemHeight
    const visibleCount = Math.min(filteredOptionsCount, maxVisibleItems)
    const listHeight = visibleCount * itemHeight
    const startIndex = virtualized ? Math.floor(scrollTop / itemHeight) : 0
    const endIndex = virtualized
      ? Math.min(startIndex + visibleCount + 2, filteredOptionsCount)
      : filteredOptionsCount
    const offsetY = virtualized ? startIndex * itemHeight : 0

    return { totalHeight, visibleCount, listHeight, startIndex, endIndex, offsetY }
  }, [filteredOptionsCount, itemHeight, maxVisibleItems, virtualized, scrollTop])
}

interface UseKeyboardNavigationOptions<T> {
  isOpen: boolean
  filteredOptions: SelectOption<T>[]
  highlightedIndex: number
  onOpen: () => void
  onClose: () => void
  onSetHighlightedIndex: (index: number) => void
  onSelect: (option: SelectOption<T>) => void
  onSetSearchQuery: (query: string) => void
}

function handleClosedKey<T>(
  e: React.KeyboardEvent,
  options: Pick<UseKeyboardNavigationOptions<T>, 'onOpen' | 'onSetSearchQuery'>,
): void {
  if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
    e.preventDefault()
    options.onOpen()
    return
  }
  /* v8 ignore next 6 */
  if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
    e.preventDefault()
    options.onSetSearchQuery(e.key)
    options.onOpen()
  }
}

function handleOpenKey<T>(
  e: React.KeyboardEvent,
  opts: UseKeyboardNavigationOptions<T>,
): void {
  const { filteredOptions, highlightedIndex, onSetHighlightedIndex, onSelect, onClose, onSetSearchQuery } = opts
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      onSetHighlightedIndex(
        highlightedIndex < filteredOptions.length - 1 ? highlightedIndex + 1 : highlightedIndex,
      )
      break
    case 'ArrowUp':
      e.preventDefault()
      onSetHighlightedIndex(highlightedIndex > 0 ? highlightedIndex - 1 : 0)
      break
    case 'Enter':
      e.preventDefault()
      if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        onSelect(filteredOptions[highlightedIndex])
      }
      break
    case 'Escape':
      e.preventDefault()
      onClose()
      onSetSearchQuery('')
      break
  }
}

export function useKeyboardNavigation<T>(opts: UseKeyboardNavigationOptions<T>): (e: React.KeyboardEvent) => void {
  const { isOpen, onOpen, onSetSearchQuery } = opts
  return useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        handleClosedKey(e, { onOpen, onSetSearchQuery })
        return
      }
      handleOpenKey(e, opts)
    },
    [isOpen, onOpen, onSetSearchQuery, opts],
  )
}

export function useFilteredOptions<T>(
  options: SelectOption<T>[],
  searchQuery: string,
  filterOption: (option: SelectOption<T>, query: string) => boolean
): SelectOption<T>[] {
  return useMemo(() => {
    if (!searchQuery) { return options }
    return options.filter((opt) => filterOption(opt, searchQuery))
  }, [options, searchQuery, filterOption])
}
